import { useEffect, useRef, useState } from "react";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import { common } from "@shared/constants";
import {
  EnhancedInfoType,
  GuestQuestionType,
} from "@shared/modules/basket/types/common/serializer-types";
import QuestionForms from "@shared/modules/checkout/components/QuestionForms/QuestionForms.web";
import { useCheckoutForms } from "@shared/modules/checkout/hooks/useCheckoutForms";
import { useEnhancedInfo } from "@shared/modules/checkout/hooks/useEnhancedInfo";
import { useCustomQuestions } from "@shared/modules/checkout/hooks/useCustomQuestions";
import Track from "@shared/modules/analytics/components/Track/Track.web";
import {
  generateInitialValuesForAddressForm,
  generateInitialValuesForCustomerForm,
  generateInitialValuesForCustomQuestionsForm,
  generateInitialValuesForEnhancedForm,
} from "./helper/CustomQuestionsHelper";
import PublicQuestionForms from "./helper/PublicQuestionForms";

export const CustomQuestionsForm = ({
  checkoutForms,
  onValid,
  onInvalid,
}: {
  checkoutForms: ReturnType<typeof useCheckoutForms>;
  onValid?: () => void;
  onInvalid?: () => void;
}) => {
  const {
    customerInfo,
    enhancedInfoQuestions,
    enhancedInfoAddress,
    itemGroups,
    isGroupSale,
    updateGuestQuestions,
  } = usePublicBasket();

  const { hasCustomQuestions, getCustomQuestions } = useCustomQuestions(
    common.DataProvider.Public,
    true
  );
  const { hasEnhancedInfo, hasAddressInfo, getEnhancedInfoQuestions } =
    useEnhancedInfo(common.DataProvider.Public, true);

  const hasMultiGuestItemGroups =
    itemGroups.filter((ig) => ig.info_required).length > 0 && !isGroupSale;

  // sort groups by ticket/membership level
  const multiGuestItemGroups = itemGroups
    .filter((ig) => ig.info_required)
    .sort(
      (a, b) =>
        (a.type ?? a.membership_level ?? 0) -
        (b.type ?? b.membership_level ?? 0)
    );

  const [initialValuesForCustomerForm] = useState(() =>
    generateInitialValuesForCustomerForm(customerInfo, itemGroups)
  );

  const [initialValuesForAddressForm] = useState(() =>
    generateInitialValuesForAddressForm(
      enhancedInfoAddress,
      itemGroups,
      hasMultiGuestItemGroups
    )
  );

  const [initialValuesForEnhancedForm] = useState(() =>
    generateInitialValuesForEnhancedForm(
      enhancedInfoQuestions,
      itemGroups,
      getEnhancedInfoQuestions,
      hasMultiGuestItemGroups
    )
  );

  const [initialValuesForCustomQuestionsForm] = useState(() =>
    generateInitialValuesForCustomQuestionsForm(
      itemGroups,
      getCustomQuestions,
      hasMultiGuestItemGroups
    )
  );

  const hasGuestQuestions =
    hasCustomQuestions() ||
    hasEnhancedInfo() ||
    hasAddressInfo() ||
    hasMultiGuestItemGroups;

  const guestQuestionsMap = useRef<
    Record<
      number,
      {
        customQuestions: Array<GuestQuestionType>;
        enhancedInfo: EnhancedInfoType;
      }
    >
  >({});

  useEffect(() => {
    (async () => {
      const hasValidForm = await checkoutForms.areFormsValid();

      if (hasValidForm) {
        onValid?.();
      } else {
        onInvalid?.();
      }
    })();
    // DO NOT COPY
    // This is WILDLY inefficient. JSON serialization, on both ends, is expensive.
    // This is here to be able to call our validation callbacks without causing an infinite loop.
    // The forms needs to get refactored to:
    // 1. prevent initialization of forms that are not needed. See useCheckoutForms.
    // 2. unnecessary duplicated structures of the component tree. Multi-guest forms are simply
    //    the same 1 group of forms (enhanced + custom questions), just repeated multiple times.
  }, [JSON.stringify(checkoutForms)]);

  return (
    <div>
      {hasGuestQuestions && (
        <>
          <Track.Impression
            analytics={{
              label: "custom-questions",
              custom_params: {
                datum: "list_view",
                has_enhanced_info: Boolean(hasEnhancedInfo()),
                has_address_info: Boolean(hasAddressInfo()),
                has_multi_guest_itemgroups: Boolean(hasMultiGuestItemGroups),
              },
            }}
          >
            <div>
              {hasMultiGuestItemGroups ? (
                multiGuestItemGroups.map((ig, index) => {
                  const igCustomQuestions = ig.info_custom_question_data ?? [];
                  const igCollectAddress =
                    ig.collect_info_fields?.collect_info_home_address;
                  const igEnhancedInfo = { ...ig.collect_info_fields };

                  return (
                    <PublicQuestionForms.QuestionsContainer
                      {...checkoutForms}
                      key={ig.id}
                      multiGuestItemGroup={ig}
                      multiGuestIndex={index + 1}
                      guestQuestionsMap={guestQuestionsMap}
                      initalValuesForCustomerForm={
                        initialValuesForCustomerForm?.[index]
                      }
                    >
                      <QuestionForms.EnhancedInfoForm
                        {...checkoutForms}
                        requireInfoCollection
                        hasAddressInfo={hasAddressInfo(igCollectAddress)}
                        hasEnhancedInfo={hasEnhancedInfo(igEnhancedInfo)}
                        enhancedInfoQuestions={getEnhancedInfoQuestions(
                          igEnhancedInfo
                        )}
                        itemGroupId={Number(ig.id)}
                        guestQuestionsMap={guestQuestionsMap}
                        initialAddressInfo={
                          initialValuesForAddressForm?.[index]
                        }
                        initialEnhancedInfo={
                          initialValuesForEnhancedForm?.[index]
                        }
                        isMultiGuest
                        onUpdateGuestQuestions={updateGuestQuestions}
                      />
                      <QuestionForms.CustomQuestionsForm
                        {...checkoutForms}
                        hasCustomQuestions={hasCustomQuestions(
                          igCustomQuestions
                        )}
                        customQuestions={getCustomQuestions(igCustomQuestions)}
                        itemGroupId={Number(ig.id)}
                        guestQuestionsMap={guestQuestionsMap}
                        initialValuesCustomQuestions={
                          initialValuesForCustomQuestionsForm?.[index]
                        }
                        isMultiGuest
                        onUpdateGuestQuestions={updateGuestQuestions}
                      />
                    </PublicQuestionForms.QuestionsContainer>
                  );
                })
              ) : (
                <PublicQuestionForms.QuestionsContainer
                  multiGuestForm={checkoutForms.multiGuestForm}
                  multiGuestIndex={0}
                  guestQuestionsMap={guestQuestionsMap}
                  initalValuesForCustomerForm={
                    initialValuesForCustomerForm?.[0]
                  }
                >
                  <QuestionForms.EnhancedInfoForm
                    {...checkoutForms}
                    requireInfoCollection
                    hasAddressInfo={hasAddressInfo()}
                    hasEnhancedInfo={hasEnhancedInfo()}
                    enhancedInfoQuestions={getEnhancedInfoQuestions()}
                    guestQuestionsMap={guestQuestionsMap}
                    initialAddressInfo={initialValuesForAddressForm?.[0]}
                    initialEnhancedInfo={initialValuesForEnhancedForm?.[0]}
                    isMultiGuest={false}
                    onUpdateGuestQuestions={updateGuestQuestions}
                  />
                  <QuestionForms.CustomQuestionsForm
                    {...checkoutForms}
                    hasCustomQuestions={hasCustomQuestions()}
                    customQuestions={getCustomQuestions()}
                    guestQuestionsMap={guestQuestionsMap}
                    initialValuesCustomQuestions={
                      initialValuesForCustomQuestionsForm?.[0]
                    }
                    isMultiGuest={false}
                    onUpdateGuestQuestions={updateGuestQuestions}
                  />
                </PublicQuestionForms.QuestionsContainer>
              )}
            </div>
          </Track.Impression>
        </>
      )}
    </div>
  );
};
